import { useEffect, useState } from 'react';

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { FormProvider, useForm } from 'react-hook-form';
import styled from 'styled-components';

import { FieldContainer } from 'core/forms';
import { Button, ComponentSizes, Dialog, GridColumn, InputField, KendoGrid, MultiSelectField, SwitchField, TextAreaField } from 'core/ui';

import { FileTaggingClient, FileTaggingRule } from '../../../core/api/client';
import { HttpClient } from '../../../core/api/client/HttpClient';
import { TagsClient } from '../../../core/api/client/TagsClient';
import { NotificationsService } from '../../../core/notifications';
import { TagModel } from '../../../models';
import { useApiQuery } from '../../api/hooks/useApiQuery';

const fileTaggingClient = new FileTaggingClient(new HttpClient());
const tagsClient = new TagsClient(new HttpClient());

// Removed executionPhases - now using worklistViewId

interface FormValues {
  name: string;
  description: string;
  active: boolean;
  priority: number;
  filterExpression: string;
  liquidTemplate: string;
  worklistViewId?: number;
  assignedTagIds: number[];
}

export function FileTaggingManagementPage() {
  const [showModal, setShowModal] = useState(false);
  const [editingRule, setEditingRule] = useState<FileTaggingRule | null>(null);
  const [testExamSUID, setTestExamSUID] = useState('');
  const [showTestDialog, setShowTestDialog] = useState(false);
  const [testingRuleId, setTestingRuleId] = useState<number | null>(null);

  const queryClient = useQueryClient();

  // Fetch file tagging rules
  const { data: rules = [], isLoading: rulesLoading } = useApiQuery({
    queryKey: ['fileTaggingRules'],
    apiFn: fileTaggingClient.getFileTaggingRules.bind(fileTaggingClient),
    params: [false], // Include inactive rules for admin
  });

  // Fetch available tags
  const { data: tags = [] } = useApiQuery({
    queryKey: ['tags'],
    apiFn: tagsClient.getAllTags.bind(tagsClient),
    params: [],
  });

  // Helper function to convert TagModel to FileTaggingClient.TagModel
  const convertToApiTagModel = (tag: TagModel): import('../../../core/api/client/FileTaggingClient').TagModel => ({
    id: tag.id,
    name: tag.name,
    description: tag.description || '',
    required: tag.required || false,
    multipleSection: tag.multipleSelection || false,
    parentTagId: tag.parentTagId || undefined,
  });

  // Create/Update mutation
  const saveRuleMutation = useMutation({
    mutationFn: async (formData: FormValues) => {
      const ruleData = {
        ...formData,
        assignedTags: formData.assignedTagIds.map((tagId) => {
          const sourceTag = tags.find((t: any) => t.id === tagId)!;
          return {
            id: 0,
            fileTaggingRuleId: 0,
            tagId,
            tag: convertToApiTagModel(sourceTag),
          };
        }),
      };

      if (editingRule) {
        return fileTaggingClient.updateFileTaggingRule(editingRule.id, { ...ruleData, id: editingRule.id });
      } else {
        return fileTaggingClient.createFileTaggingRule(ruleData);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['fileTaggingRules'] });
      setShowModal(false);
      setEditingRule(null);
      NotificationsService.displaySuccess(editingRule ? 'Rule updated successfully' : 'Rule created successfully');
    },
    onError: (error: any) => {
      NotificationsService.displayError('Failed to save rule: ' + (error?.message || 'Unknown error'));
    },
  });

  // Delete mutation
  const deleteRuleMutation = useMutation({
    mutationFn: (id: number) => fileTaggingClient.deleteFileTaggingRule(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['fileTaggingRules'] });
      NotificationsService.displaySuccess('Rule deleted successfully');
    },
    onError: (error: any) => {
      NotificationsService.displayError('Failed to delete rule: ' + (error?.message || 'Unknown error'));
    },
  });

  // Test rule mutation
  const testRuleMutation = useMutation({
    mutationFn: ({ ruleId, examSUID }: { ruleId: number; examSUID: string }) => fileTaggingClient.testFileTaggingRule(ruleId, { examSUID }),
    onSuccess: (result: any) => {
      NotificationsService.displaySuccess(`Test result: ${result.wouldApplyTags ? 'Rule would apply tags' : 'Rule would NOT apply tags'}`);
      setShowTestDialog(false);
      setTestExamSUID('');
    },
    onError: (error: any) => {
      NotificationsService.displayError('Failed to test rule: ' + (error?.message || 'Unknown error'));
    },
  });

  const handleAddRule = () => {
    setEditingRule(null);
    setShowModal(true);
  };

  const handleEditRule = (rule: FileTaggingRule) => {
    setEditingRule(rule);
    setShowModal(true);
  };

  const handleDeleteRule = (id: number) => {
    if (confirm('Are you sure you want to delete this rule?')) {
      deleteRuleMutation.mutate(id);
    }
  };

  const handleTestRule = (ruleId: number) => {
    setTestingRuleId(ruleId);
    setShowTestDialog(true);
  };

  const handleFormSubmit = (dataItem: FormValues) => {
    saveRuleMutation.mutate(dataItem);
  };

  const initialFormValues: FormValues = {
    name: editingRule?.name || '',
    description: editingRule?.description || '',
    active: editingRule?.active ?? true,
    priority: editingRule?.priority || 0,
    filterExpression: editingRule?.filterExpression || '',
    liquidTemplate: editingRule?.liquidTemplate || '',
    worklistViewId: editingRule?.worklistViewId,
    assignedTagIds: editingRule?.assignedTags?.map((rt) => rt.tagId) || [],
  };

  const formMethods = useForm<FormValues>({
    defaultValues: initialFormValues,
  });

  // Reset form when editing a different rule
  useEffect(() => {
    formMethods.reset(initialFormValues);
  }, [editingRule, formMethods]);

  return (
    <div style={{ padding: '20px' }}>
      <h2>File Tagging Rule Management</h2>
      <p>Manage automated file tagging rules that apply during upload and DICOM processing.</p>

      <div style={{ marginBottom: '20px' }}>
        <Button onClick={handleAddRule} themeColor="primary">
          Add New Rule
        </Button>
      </div>

      <KendoGrid data={rules} style={{ height: '600px' }}>
        <GridColumn field="name" title="Name" width="200px" />
        <GridColumn field="description" title="Description" width="250px" />
        <GridColumn
          field="worklistViewId"
          title="Worklist View"
          width="150px"
          cell={(props) => <td>{props.dataItem.worklistViewId ? `View ${props.dataItem.worklistViewId}` : 'All Views'}</td>}
        />
        <GridColumn field="priority" title="Priority" width="100px" />
        <GridColumn
          field="active"
          title="Active"
          width="100px"
          cell={(props) => (
            <td>
              <input type="checkbox" checked={props.dataItem.active} disabled />
            </td>
          )}
        />
        <GridColumn
          field="assignedTags"
          title="Tags"
          width="200px"
          cell={(props) => <td>{props.dataItem.assignedTags?.map((rt: any) => rt.tag?.name).join(', ') || 'None'}</td>}
        />
        <GridColumn
          title="Actions"
          width="200px"
          cell={(props) => (
            <td>
              <Button size={ComponentSizes.SMALL} onClick={() => handleEditRule(props.dataItem)} style={{ marginRight: '5px' }}>
                Edit
              </Button>
              <Button size={ComponentSizes.SMALL} onClick={() => handleTestRule(props.dataItem.id)} style={{ marginRight: '5px' }} themeColor="info">
                Test
              </Button>
              <Button size={ComponentSizes.SMALL} onClick={() => handleDeleteRule(props.dataItem.id)} themeColor="error">
                Delete
              </Button>
            </td>
          )}
        />
      </KendoGrid>

      {/* Add/Edit Modal */}
      {showModal && (
        <Dialog title={editingRule ? 'Edit File Tagging Rule' : 'Add File Tagging Rule'} onClose={() => setShowModal(false)}>
          <FormProvider {...formMethods}>
            <form onSubmit={formMethods.handleSubmit(handleFormSubmit)} style={{ padding: '20px' }}>
              <FieldContainer>
                <InputField name="name" label="Rule Name" required style={{ width: '100%', marginBottom: '15px' }} />
              </FieldContainer>

              <FieldContainer>
                <TextAreaField name="description" label="Description" rows={3} style={{ width: '100%', marginBottom: '15px' }} />
              </FieldContainer>

              <div style={{ display: 'flex', gap: '20px', marginBottom: '15px' }}>
                <FieldContainer>
                  <InputField name="priority" label="Priority (lower = higher priority)" type="number" style={{ width: '200px' }} />
                </FieldContainer>

                <FieldContainer>
                  <InputField
                    name="worklistViewId"
                    label="Worklist View ID (optional)"
                    type="number"
                    placeholder="Leave empty for all views"
                    style={{ width: '200px' }}
                  />
                </FieldContainer>
              </div>

              <FieldContainer>
                <SwitchField name="active" label="Active" />
              </FieldContainer>

              <FieldContainer>
                <TextAreaField
                  name="filterExpression"
                  label="OData Filter Expression (e.g., Modality~eq~CT)"
                  placeholder="Modality~eq~CT or StudyDate~gte~2024-01-01"
                  rows={3}
                  style={{ width: '100%', marginBottom: '15px' }}
                />
              </FieldContainer>

              <FieldContainer>
                <TextAreaField
                  name="liquidTemplate"
                  label="Liquid Template (for complex logic)"
                  placeholder="{% if Modality == 'CT' and PatientAge >= 65 %}{{ true }}{% else %}{{ false }}{% endif %}"
                  rows={4}
                  style={{ width: '100%', marginBottom: '15px' }}
                />
              </FieldContainer>

              <FieldContainer>
                <MultiSelectField
                  name="assignedTagIds"
                  data={tags}
                  textField="name"
                  dataItemKey="id"
                  label="Tags to Apply"
                  placeholder="Select tags to apply when rule matches"
                  style={{ width: '100%', marginBottom: '20px' }}
                />
              </FieldContainer>

              <div style={{ marginTop: '20px', borderTop: '1px solid #ccc', paddingTop: '15px' }}>
                <h4>Rule Logic Examples:</h4>
                <ul style={{ fontSize: '12px', color: '#666' }}>
                  <li>
                    <strong>OData Filter:</strong> Modality~eq~CT (matches CT scans)
                  </li>
                  <li>
                    <strong>OData Filter:</strong> StudyDate~gte~2024-01-01 (matches studies from 2024)
                  </li>
                  <li>
                    <strong>Liquid Template:</strong> {`{% if PatientAge >= 65 %}{{ true }}{% else %}{{ false }}{% endif %}`}
                  </li>
                  <li>
                    <strong>Complex Liquid:</strong> {`{% if Modality == 'CT' and BodyPartExamined contains 'Chest' %}{{ true }}{% endif %}`}
                  </li>
                </ul>
              </div>

              <StyledButtonsContainer>
                <Button onClick={() => setShowModal(false)} style={{ marginRight: '10px' }}>
                  Cancel
                </Button>
                <Button type="submit" themeColor="primary" disabled={saveRuleMutation.isPending}>
                  {saveRuleMutation.isPending ? 'Saving...' : 'Save'}
                </Button>
              </StyledButtonsContainer>
            </form>
          </FormProvider>
        </Dialog>
      )}

      {/* Test Rule Dialog */}
      {showTestDialog && (
        <Dialog title="Test Tagging Rule" onClose={() => setShowTestDialog(false)}>
          <div style={{ padding: '20px' }}>
            <p>Enter an Exam SUID to test this rule against:</p>
            <InputField
              name="testExamSUID"
              value={testExamSUID}
              onChange={(e) => setTestExamSUID(e.target.value)}
              placeholder="Exam SUID"
              style={{ width: '100%', marginBottom: '20px' }}
            />
          </div>

          <StyledButtonsContainer>
            <Button onClick={() => setShowTestDialog(false)}>Cancel</Button>
            <Button
              onClick={() =>
                testingRuleId &&
                testRuleMutation.mutate({
                  ruleId: testingRuleId,
                  examSUID: testExamSUID,
                })
              }
              themeColor="primary"
              disabled={!testExamSUID.trim() || testRuleMutation.isPending}
            >
              {testRuleMutation.isPending ? 'Testing...' : 'Test Rule'}
            </Button>
          </StyledButtonsContainer>
        </Dialog>
      )}
    </div>
  );
}

const StyledButtonsContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 20px;
  padding-top: 15px;
  border-top: 1px solid #ccc;
`;
